import { Capacitor, registerPlugin } from '@capacitor/core';
import type { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { api, socket } from './api';

// ---------------------------------------------------------------- geolocation
/** Present only inside the Android shell (apps/driver-android): keeps tracking with the screen off. */
const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');
const nativeTracking = Capacitor.isPluginAvailable('BackgroundGeolocation');
let nativeWatcher: Promise<string | null> | null = null;
let watchId: number | null = null;
let lastSent = 0;
let lastPos: { lat: number; lng: number } | null = null;
/** The last position sent was the quick approximate one: the first precise fix replaces it at once. */
let sentApprox = false;
let refreshTimer: ReturnType<typeof setInterval> | null = null;
/** The last fix read (sent or not): what a silent alert carries when it goes out. */
let lastFix: { lat: number; lng: number; accuracyM: number; at: number } | null = null;
/** A silent alert is active (lib/panic.ts): one position every PANIC_INTERVAL_MS, even standing still. */
let panicMode = false;
let panicTimer: ReturnType<typeof setInterval> | null = null;
const PANIC_INTERVAL_MS = 5000;
const MIN_INTERVAL_MS = 5000;
/**
 * The watch only fires when the device moves: a parked phone (or a laptop) goes silent, and
 * dispatch trusts a position for 3 min (15 min while the app keeps its heartbeat). Without news for
 * this long, a quick reading is requested and sent.
 */
const REFRESH_MS = 30_000;
/** A periodic reading that fails while the last position sent is older than this warns the driver. */
const STALE_MS = 90_000;
/** Android shell: without a first fix this long, the phone's location is probably off. */
const NO_FIX_MS = 20_000;

/** True inside the Android shell (apps/driver-android). */
export const isNativeApp = nativeTracking;
/** Android shell: opens this app's system settings (permissions). */
export const openAppSettings = () => BackgroundGeolocation.openSettings();

/** Android shell: while someone talks on the radio, music and navigation duck and the voice uses the loudspeaker. */
const RadioAudio = registerPlugin<{ begin(): Promise<void>; end(): Promise<void> }>('RadioAudio');
const nativeRadioAudio = Capacitor.isPluginAvailable('RadioAudio');
export function radioAudio(hearing: boolean) {
  if (nativeRadioAudio) void (hearing ? RadioAudio.begin() : RadioAudio.end()).catch(() => undefined);
}

export type GeoState = 'off' | 'active' | 'denied' | 'unavailable';

type Fix = { lat: number; lng: number; heading: number | null; speedKmh: number | null; accuracyM: number };

function send(p: GeolocationPosition) {
  sendFix({ lat: p.coords.latitude, lng: p.coords.longitude, heading: p.coords.heading, speedKmh: p.coords.speed !== null ? p.coords.speed * 3.6 : null, accuracyM: p.coords.accuracy });
}

function sendFix(msg: Fix) {
  lastFix = { lat: msg.lat, lng: msg.lng, accuracyM: msg.accuracyM, at: Date.now() };
  if (socket.state === 'open') socket.send({ type: 'location', ...msg });
  else void api.post('/driver/location', msg).catch(() => undefined);
}

/** Quick approximate reading (network, cached up to REFRESH_MS); a precise fix replaces it at once. */
function quickFix(id: number, onState: (s: GeoState) => void) {
  const asked = lastSent;
  navigator.geolocation.getCurrentPosition(
    (p) => {
      // only while tracking is still the same one and nothing was sent in the meantime
      if (watchId !== id || lastSent !== asked) return;
      onState('active');
      lastSent = Date.now();
      lastPos = { lat: p.coords.latitude, lng: p.coords.longitude };
      sentApprox = true;
      send(p);
    },
    (err) => {
      // a single slow reading is normal; a position going stale is not (dispatch stops trusting it)
      if (watchId !== id) return;
      if (err.code === err.PERMISSION_DENIED) onState('denied');
      else if (Date.now() - lastSent > STALE_MS) onState('unavailable');
    },
    { enableHighAccuracy: false, maximumAge: REFRESH_MS, timeout: 10_000 },
  );
}

/**
 * Streams the device position over the socket (HTTP fallback when the socket is down). A precise
 * GPS fix can take 5–30 s after connecting, so a quick approximate position goes first and the
 * dispatcher sees the driver on the map right away; the GPS replaces it. While the watch is silent
 * (device still), a new quick reading goes out every REFRESH_MS so the driver stays dispatchable.
 */
export function startLocation(onState: (s: GeoState) => void) {
  if (nativeTracking) return startNative(onState);
  if (!('geolocation' in navigator)) return onState('unavailable');
  if (watchId !== null) return onState('active');
  lastSent = 0;
  lastPos = null;
  sentApprox = false;
  watchId = navigator.geolocation.watchPosition(
    (p) => {
      onState('active');
      const now = Date.now();
      const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
      const moved = !lastPos || Math.abs(pos.lat - lastPos.lat) + Math.abs(pos.lng - lastPos.lng) > 0.0002;
      if (!sentApprox) {
        if (now - lastSent < MIN_INTERVAL_MS && !moved) return;
        if (now - lastSent < 2000) return;
      }
      lastSent = now;
      lastPos = pos;
      sentApprox = false;
      send(p);
    },
    (err) => onState(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable'),
    { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
  );
  const id = watchId;
  quickFix(id, onState);
  refreshTimer = setInterval(() => {
    if (Date.now() - lastSent >= REFRESH_MS - 1000) quickFix(id, onState);
  }, REFRESH_MS);
}

/**
 * Android shell: a foreground service (with its notification) keeps the GPS and this page running
 * with the screen off or another app in front. Fixes arrive continuously, so they are thinned here:
 * moving, one every 2 s; still, one every REFRESH_MS so the driver stays dispatchable. A GPS fix can
 * take 5–30 s after connecting, so the phone's last known position goes first when it is recent.
 */
function startNative(onState: (s: GeoState) => void) {
  if (nativeWatcher) return onState('active');
  lastSent = 0;
  lastPos = null;
  sentApprox = false;
  let first = true;
  // the plugin waits silently when the phone's location is off: no first fix in NO_FIX_MS, or a
  // position going stale, warns the driver (the watcher keeps trying)
  const startedAt = Date.now();
  refreshTimer = setInterval(() => {
    if (lastSent ? Date.now() - lastSent > STALE_MS : Date.now() - startedAt > NO_FIX_MS) onState('unavailable');
  }, 5000);
  nativeWatcher = BackgroundGeolocation.addWatcher(
    { backgroundTitle: 'Central: en turno', backgroundMessage: 'Compartiendo tu ubicación para recibir servicios', requestPermissions: true, stale: true, distanceFilter: 0 },
    (loc, err) => {
      if (err) return onState(err.code === 'NOT_AUTHORIZED' ? 'denied' : 'unavailable');
      if (!loc) return;
      const now = Date.now();
      // `stale`: the first reading may be the last known position, from hours ago in another place
      const old = first && loc.time !== null && now - loc.time > STALE_MS;
      first = false;
      if (old) return;
      onState('active');
      const pos = { lat: loc.latitude, lng: loc.longitude };
      const moved = !lastPos || Math.abs(pos.lat - lastPos.lat) + Math.abs(pos.lng - lastPos.lng) > 0.0002;
      // a slow crawl takes long to add up to `moved`: the GPS speed (m/s) tells it apart from standing
      const moving = moved || (loc.speed ?? 0) > 1;
      // the reading after the last known position is the first live one: it replaces it at once
      if (!sentApprox && now - lastSent < (moving ? 2000 : panicMode ? PANIC_INTERVAL_MS - 500 : REFRESH_MS - 1000)) return;
      sentApprox = !lastSent && loc.time !== null && now - loc.time > 5000;
      lastSent = now;
      lastPos = pos;
      sendFix({ ...pos, heading: loc.bearing, speedKmh: loc.speed !== null ? loc.speed * 3.6 : null, accuracyM: loc.accuracy });
    },
  ).catch(() => {
    onState('unavailable');
    return null;
  });
}

export function stopLocation() {
  if (nativeWatcher) {
    const w = nativeWatcher;
    nativeWatcher = null;
    void w.then((id) => (id ? BackgroundGeolocation.removeWatcher({ id }) : undefined));
  }
  if (watchId !== null) navigator.geolocation.clearWatch(watchId);
  if (refreshTimer !== null) clearInterval(refreshTimer);
  watchId = null;
  refreshTimer = null;
}

/** The last position read in the last 2 minutes, if any. */
export function lastPosition() {
  return lastFix && Date.now() - lastFix.at < 120_000 ? lastFix : null;
}

/**
 * While a silent alert is active the central follows the driver: a precise reading every
 * PANIC_INTERVAL_MS, whether the driver is moving, standing or even off shift (no tracking running).
 */
export function setPanicTracking(on: boolean) {
  if (on === panicMode) return;
  panicMode = on;
  if (panicTimer !== null) clearInterval(panicTimer);
  panicTimer = null;
  if (!on || !('geolocation' in navigator)) return;
  const read = () => {
    if (Date.now() - lastSent < PANIC_INTERVAL_MS - 500) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        lastSent = Date.now();
        lastPos = { lat: p.coords.latitude, lng: p.coords.longitude };
        send(p);
      },
      () => undefined,
      { enableHighAccuracy: true, maximumAge: PANIC_INTERVAL_MS - 1000, timeout: PANIC_INTERVAL_MS },
    );
  };
  read();
  panicTimer = setInterval(read, PANIC_INTERVAL_MS);
}

// tracking lives in this module's state and is started once by the store: a hot update would leave
// the old watcher running without the new code, so editing this file reloads the page instead
if (import.meta.hot) import.meta.hot.accept(() => location.reload());

// ---------------------------------------------------------------- service worker & push
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export type PushState = 'unsupported' | 'denied' | 'disabled' | 'enabled' | 'no-server-key';

/**
 * Android shell: notifications are native. Local ones (alerts.ts) work while the page is alive; FCM
 * wakes the phone with the app closed. Registering with FCM in an APK built without
 * google-services.json crashes the app, so the shell says it has it in its user agent
 * (capacitor.config.ts).
 */
const nativeNotifications = Capacitor.isPluginAvailable('LocalNotifications');
const nativePush = Capacitor.isPluginAvailable('PushNotifications') && navigator.userAgent.includes('DespachadorPush');
let nativeListening = false;

/** This APK can be woken by the server with the app closed (built with Firebase). */
export const hasNativePush = nativePush;

/** Loud for offers and calls, default for notices, silent for the notice that closes an offer. Idempotent. */
function createChannels() {
  return Promise.all([
    LocalNotifications.createChannel({ id: 'offers', name: 'Ofertas y llamadas', description: 'Servicios ofrecidos y llamadas de la central', importance: 5, visibility: 1, vibration: true }),
    LocalNotifications.createChannel({ id: 'notices', name: 'Avisos', description: 'Cuota, documentos y cuenta', importance: 3, visibility: 0 }),
    LocalNotifications.createChannel({ id: 'quiet', name: 'Ofertas cerradas', description: 'Servicios que ya no están disponibles', importance: 2, visibility: 0 }),
  ]).catch(() => undefined);
}

/** Gets this phone's FCM token and hands it to the API under the current session. */
async function registerNativePush() {
  if (!nativeListening) {
    nativeListening = true;
    await PushNotifications.addListener('registration', (t) => void api.post('/me/push-subscriptions', { fcmToken: t.value }).catch(() => undefined));
    // tapped with the app in the background or closed: offers are picked up by the store's refresh,
    // any other notice opens its screen (only paths of this app)
    await PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
      const d = (a.notification.data ?? {}) as { category?: string; url?: string };
      if (d.category !== 'OFFER' && typeof d.url === 'string' && d.url.startsWith('/') && !d.url.startsWith('//') && d.url !== '/') {
        history.pushState({}, '', d.url);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      window.dispatchEvent(new Event('driver:push-opened'));
    });
  }
  await PushNotifications.register();
}

/**
 * Android shell, page alive: the offer already has its local notification (alerts.ts), so the copy
 * that FCM delivers a moment later, and the notice that closes it, are taken off the tray.
 */
export async function clearOfferPushes() {
  if (!nativePush) return;
  try {
    const { notifications } = await PushNotifications.getDeliveredNotifications();
    const offers = notifications.filter((n) => n.tag?.startsWith('offer-'));
    if (offers.length) await PushNotifications.removeDeliveredNotifications({ notifications: offers });
  } catch {
    // best effort
  }
}

export async function pushState(): Promise<PushState> {
  if (nativeNotifications) {
    const p = (await LocalNotifications.checkPermissions().catch(() => null))?.display;
    return p === 'granted' ? 'enabled' : p === 'denied' ? 'denied' : 'disabled';
  }
  if (!('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker?.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'enabled' : 'disabled';
}

/**
 * On start, with a session: this device is registered again under the user and session now in use.
 * A push only goes out while the session that registered the device is alive, and a phone that
 * changes hands must stop carrying the previous driver's notices. Never asks for permission.
 */
export async function syncPush(serverNative: boolean): Promise<void> {
  try {
    if (nativeNotifications) {
      if ((await LocalNotifications.checkPermissions()).display !== 'granted') return;
      await createChannels();
      if (nativePush && serverNative) await registerNativePush();
      return;
    }
    if (!('PushManager' in window) || !('Notification' in window) || Notification.permission !== 'granted') return;
    const sub = await (await navigator.serviceWorker?.getRegistration())?.pushManager.getSubscription();
    if (!sub) return;
    const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
    await api.post('/me/push-subscriptions', { endpoint: json.endpoint, keys: json.keys });
  } catch {
    // offline or blocked: the next start tries again
  }
}

/**
 * Asks permission (must be a user gesture) and registers the subscription with the API. In the
 * Android shell, the permission is the system's (Android 13+ shows nothing without it).
 */
export async function enablePush(publicKey: string | null, serverNative = false): Promise<PushState> {
  if (nativeNotifications) {
    const p = (await LocalNotifications.requestPermissions().catch(() => null))?.display;
    if (p !== 'granted') return p === 'denied' ? 'denied' : 'disabled';
    await createChannels();
    if (nativePush && serverNative) await registerNativePush().catch(() => undefined);
    return 'enabled';
  }
  if (!('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return 'denied';
  if (!publicKey) return 'no-server-key';
  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
  if (!reg) return 'unsupported';
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.post('/me/push-subscriptions', { endpoint: json.endpoint, keys: json.keys });
  return 'enabled';
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.del('/me/push-subscriptions', { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe();
  }
}

// ---------------------------------------------------------------- install prompt
type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
let deferred: InstallEvent | null = null;
const installListeners = new Set<(v: boolean) => void>();
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as InstallEvent;
  installListeners.forEach((l) => l(true));
});
export const canInstall = () => !!deferred;
export const onInstallable = (fn: (v: boolean) => void) => {
  installListeners.add(fn);
  return () => void installListeners.delete(fn);
};
export async function install() {
  if (!deferred) return false;
  await deferred.prompt();
  const r = await deferred.userChoice;
  deferred = null;
  installListeners.forEach((l) => l(false));
  return r.outcome === 'accepted';
}
export const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches;
